import React, { useState, useEffect, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { Input } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'

const FIELD_TYPES = {
  text: 'text',
  decimal: 'decimal',
  integer: 'integer'
}

const INPUT_TYPES = {
  text: 'text',
  decimal: 'decimal',
  numeric: 'numeric',
  number: 'number'
}

const createAnswerObjectsArr = (question, responses, id) => {
  const { choices } = question
  return Object.keys(responses).map((choiceId) => {
    return {
      label: choices[choiceId].label,
      value: responses[choiceId],
      question: id,
    }
  })
}

const persistMultipleFieldSubmission = (props, responses, completed) => {
  try {
    const { question, id, webSurvey, completeQuestion } = props
    if (typeof completeQuestion !== 'function' || !webSurvey) return
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = createAnswerObjectsArr(question, responses, id)
    const submission = {
      id,
      label,
      name: label,
      type,
      required,
      state: completed ? 'ANSWERED' : 'SKIPPED',
      answer,
    }
    completeQuestion(submission, webSurvey.order?.[0])
  } catch (e) {
    console.warn('MultipleField autosave failed', { error: e })
  }
}

const onCompleteMultipleFieldQuestion = (props, responses, completed) => {
  const {
    question,
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const { label, type, attributes } = question
  const { required } = attributes
  const answer = createAnswerObjectsArr(question, responses, id)
  const submission = {
    id,
    label,
    name: label,
    type,
    required,
    state: completed ? 'ANSWERED' : 'SKIPPED',
    answer
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
}

const renderInput = ({
  choiceId,
  inputMode,
  max,
  min,
  maxlength,
  inputWidth,
  onInput,
  pattern,
  posttext,
  responses,
  step,
  type
}) => {
  return (
    <Input
      className={inputWidth}
      inputProps={{
        inputMode,
        pattern: pattern || null
      }}
      key={choiceId}
      onChange={(val) => {
        let response = ''
        if (val !== '') {
          response = type === INPUT_TYPES.text ? val : Number(val)
        }
        onInput({
          ...responses,
          [choiceId]: response
        })
      }}
      maxlength={maxlength}
      max={max}
      min={min}
      posttext={posttext}
      type={type}
      step={step || null}
      value={responses[choiceId] === undefined ? '' : responses[choiceId]}
    />
  )
}

const renderFields = (
  props,
  choicesOrder,
  choices,
  inputWidth,
  required,
  completed,
  responses,
  onInput
) => {
  const {
    id,
    strings,
    renderBackButton,
    question,
    submissionSurvey,
    webSurvey,
    isConsent,
    isLastQuestion,
    submissionLoading,
    isSibling = false,
  } = props
  return (
    <div className='choices-container flexed column'>
      <div>
        {choicesOrder.map((choiceId) => {
          const choice = choices[choiceId]
          const sharedProps = {
            choiceId,
            inputWidth,
            posttext: choice.choice_label,
            responses,
            onInput
          }
          const numberProps = {
            max: choice.max,
            min: choice.min,
            type: INPUT_TYPES.number
          }
          let inputField = <div key={choiceId} />
          if (choice.type === FIELD_TYPES.text) {
            inputField = renderInput({
              ...sharedProps,
              inputMode: INPUT_TYPES.text,
              maxlength: choice.max,
              type: INPUT_TYPES.text
            })
          }
          if (choice.type === FIELD_TYPES.integer) {
            inputField = renderInput({
              ...sharedProps,
              ...numberProps,
              inputMode: INPUT_TYPES.numeric,
              pattern: '[0-9]*',
              step: 1
            })
          }
          if (choice.type === FIELD_TYPES.decimal) {
            inputField = renderInput({
              ...sharedProps,
              ...numberProps,
              inputMode: INPUT_TYPES.decimal,
              pattern: '[0-9.]*',
              step: 0.1
            })
          }
          return inputField
        })}
      </div>
      {!isSibling && (
        <QuestionFooter
          completed={completed}
          id={id}
          isConsent={isConsent}
          isLastQuestion={isLastQuestion}
          loading={submissionLoading}
          onComplete={() => {
            onCompleteMultipleFieldQuestion(props, responses, completed)
          }}
          question={question}
          renderBackButton={renderBackButton}
          strings={strings}
          submissionSurvey={submissionSurvey}
          webSurvey={webSurvey}
        />
      )}
    </div>
  )
}

const isMultipleFieldChoiceFilled = (value) =>
  value !== undefined && value !== null && value !== ''

const multipleFieldIsComplete = (choicesOrder, responses) =>
  choicesOrder.length > 0 &&
  choicesOrder.every((choiceId) =>
    isMultipleFieldChoiceFilled(responses[choiceId]),
  )

const MultipleField = forwardRef((props, ref) => {
  const {
    className,
    currentSurvey,
    id,
    question,
    setAllRequiredAnswered,
    storedAnswer,
  } = props
  const [responses, setResponses] = useState({})
  const { attributes, choices, label } = question
  const choicesOrder = question.choices_order
  const { inputWidth, required } = attributes
  const completed = multipleFieldIsComplete(choicesOrder, responses)
  const _className = className ? ` ${className}` : ''

  useEffect(() => {
    if (!Array.isArray(storedAnswer) || !storedAnswer.length) return
    const co = choicesOrder || []
    const fromStored = {}

    const mapRowToChoice = (choiceId, row) => {
      if (!row || (row.value === undefined && row.value !== 0)) return
      const choice = choices[choiceId]
      let v = row.value
      if (
        choice?.type === FIELD_TYPES.integer ||
        choice?.type === FIELD_TYPES.decimal
      ) {
        const n = Number(v)
        v = v === '' || Number.isNaN(n) ? '' : n
      }
      fromStored[choiceId] = v
    }

    if (storedAnswer.length === co.length) {
      co.forEach((choiceId, idx) => mapRowToChoice(choiceId, storedAnswer[idx]))
    } else {
      storedAnswer.forEach((row) => {
        const choiceId = co.find((cid) => choices[cid]?.label === row?.label)
        if (choiceId != null) mapRowToChoice(choiceId, row)
      })
    }

    if (Object.keys(fromStored).length === 0) return
    setResponses(fromStored)
    const done = multipleFieldIsComplete(choicesOrder, fromStored)
    if (!props.suppressAutoAdvance) {
      onCompleteMultipleFieldQuestion(props, fromStored, done)
    } else {
      persistMultipleFieldSubmission(props, fromStored, done)
    }
  }, [])

  const onInput = (answers) => {
    setAllRequiredAnswered(false, id)
    setResponses(answers)
    const done = multipleFieldIsComplete(choicesOrder, answers)
    persistMultipleFieldSubmission(props, answers, done)
  }

  return (
    <div ref={ref}>
      <SurveyQuestion id={id}>
        <MakeScrollable>
          <div className={`survey-question multiple-field${_className}`}>
            <p className='question-label'>
              <span
                dangerouslySetInnerHTML={{
                  __html: formatAndSanitizeHTML(label)
                }}
              />
            </p>
            {renderFields(
              props,
              choicesOrder,
              choices,
              inputWidth,
              required,
              completed,
              responses,
              onInput
            )}
          </div>
        </MakeScrollable>
      </SurveyQuestion>
    </div>
  )
})

export default MultipleField

renderInput.propTypes = {
  choiceId: PropTypes.string,
  inputMode: PropTypes.string,
  inputWidth: PropTypes.oneOf(['long', 'medium', 'short']),
  max: PropTypes.number,
  min: PropTypes.number,
  maxlength: PropTypes.number,
  onInput: PropTypes.func,
  pattern: PropTypes.string,
  posttext: PropTypes.string,
  responses: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  step: PropTypes.number,
  type: PropTypes.string
}

renderFields.propTypes = {
  id: PropTypes.number,
  renderBackButton: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string)
}

MultipleField.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  question: PropTypes.object,
  setAllRequiredAnswered: PropTypes.func,
  storedAnswer: PropTypes.arrayOf(PropTypes.object),
}
